import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { collaboratorAuthHeader } from "./useCollaborator";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Server-side favorites for interactive galleries.
 *
 * Models two parallel slices of state:
 *   mine    : Set<photo_id>                              — photos *I* have hearted
 *   byPhoto : { [photo_id]: { count, collaborators[] }}  — everyone-aggregate
 *
 * `mine` drives the heart icon's filled/outline state on each photo. `byPhoto`
 * drives the count badge + "picked by" hover list.
 *
 * Optimistic toggle: we mutate `mine` + bump `byPhoto[id].count` immediately,
 * then reconcile from the server's response. On failure we roll back.
 */
export default function useGalleryFavorites({ slug, token, enabled }) {
  const [mine, setMine] = useState(() => new Set());
  const [byPhoto, setByPhoto] = useState({});
  const [loaded, setLoaded] = useState(false);

  const headers = collaboratorAuthHeader(token);

  const loadAll = useCallback(async () => {
    if (!enabled || !token) { setMine(new Set()); setByPhoto({}); setLoaded(false); return; }
    try {
      const [mineRes, everyoneRes] = await Promise.all([
        axios.get(`${API}/galleries/${slug}/favorites?view=mine`, { headers }),
        axios.get(`${API}/galleries/${slug}/favorites?view=everyone`, { headers }),
      ]);
      setMine(new Set(mineRes.data?.photo_ids || []));
      const map = {};
      for (const p of (everyoneRes.data?.photos || [])) {
        map[p.photo_id] = { count: p.count, collaborators: p.collaborators || [] };
      }
      setByPhoto(map);
      setLoaded(true);
    } catch {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token, enabled]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const isMine = useCallback((photoId) => mine.has(photoId), [mine]);
  const getEntry = useCallback((photoId) => byPhoto[photoId] || { count: 0, collaborators: [] }, [byPhoto]);

  const toggle = useCallback(async (photoId, displayName, role) => {
    if (!enabled || !token) return;
    const currentlyMine = mine.has(photoId);
    const next = !currentlyMine;

    // Optimistic local update.
    const prevMine = mine;
    const prevByPhoto = byPhoto;
    setMine((s) => {
      const ns = new Set(s);
      if (next) ns.add(photoId); else ns.delete(photoId);
      return ns;
    });
    setByPhoto((bp) => {
      const cur = bp[photoId] || { count: 0, collaborators: [] };
      const filteredCols = (cur.collaborators || []).filter(c => c.display_name !== displayName || c.role !== role);
      return {
        ...bp,
        [photoId]: next
          ? { count: cur.count + (currentlyMine ? 0 : 1), collaborators: [...filteredCols, { display_name: displayName, role }] }
          : { count: Math.max(0, cur.count - 1), collaborators: filteredCols },
      };
    });

    try {
      const url = `${API}/galleries/${slug}/photos/${photoId}/favorite`;
      const { data } = next
        ? await axios.post(url, {}, { headers })
        : await axios.delete(url, { headers });
      // Reconcile authoritative count.
      setByPhoto((bp) => ({
        ...bp,
        [photoId]: {
          count: data.total_count,
          collaborators: bp[photoId]?.collaborators || [],
        },
      }));
    } catch {
      // Rollback on error.
      setMine(prevMine);
      setByPhoto(prevByPhoto);
    }
  }, [slug, token, enabled, mine, byPhoto, headers]);

  return { mine, byPhoto, isMine, getEntry, toggle, refresh: loadAll, loaded };
}
